import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { buildApiUrl } from '../../services/apiConfig';
import { useState } from 'react';

let customerId;

// Helper function to get customer ID with fallback logic
const getCustomerId = (fallbackUserId) => {
    // Try sessionStorage first
    const storedCustomerId = sessionStorage.getItem('customer_id');
    if (storedCustomerId) return storedCustomerId;

    // Then check module-level variable
    if (customerId) return customerId;

    // Finally use fallback
    return fallbackUserId;
};

// Initialize customer ID from AUTH API
export const initializeCustomerId = async (userId) => {
    try {
        // Check if already initialized
        const existing = sessionStorage.getItem('customer_id');
        if (existing) {
            customerId = existing;
            return existing;
        }

        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const response = await fetch(buildApiUrl('AUTH', `/get-client-info/all?user_id=${storedUserId}`));

        if (!response.ok) {
            return userId; // Fallback to userId
        }

        const data = await response.json();
        if (data.clients && Array.isArray(data.clients) && data.clients.length > 0) {
            customerId = data.clients[0].id;
            sessionStorage.setItem('customer_id', customerId);
            return customerId;
        }

        return userId;
    } catch (error) {
        return userId;
    }
};

// Cache for NOVA data with timestamp
const novaCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Generic function to fetch data based on data source type
export const fetchWidgetData = async (widgetType, dataSource, userId, useCaseId = 'wealth_management') => {
    try {
        switch (dataSource) {
            case 'DATABASE':
                return fetchDatabaseData(widgetType, userId);
            case 'SQL':
                return fetchSqlData(widgetType, userId, useCaseId);
            case 'NOVA':
                return fetchNovaData(widgetType, userId, useCaseId);
            default:
                return fetchFallbackData(widgetType);
        }
    } catch (error) {
        return fetchFallbackData(widgetType);
    }
};

// Database source - fetch client data from AUTH API
const fetchDatabaseData = async (widgetType, userId) => {
    try {
        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const response = await fetch(buildApiUrl('AUTH', `/get-client-info/all?user_id=${storedUserId}`));

        if (!response.ok) {
            throw new Error(`API responded with status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.clients || !Array.isArray(data.clients)) {
            throw new Error("Invalid data format received from API");
        }

        // Find the client data for the current user
        const clientData = data.clients.find(client => client.id === userId) || data.clients[0];
        customerId = data.clients[0].id;
        // Store in sessionStorage for consistent access
        sessionStorage.setItem('customer_id', customerId);

        // Process the data based on widget type
        switch (widgetType) {
            case 'portfolio':
                return {
                    totalAum: clientData.aum || 2845000,
                    ytdReturn: clientData.ytd_return || 12.8,
                    avgYield: clientData.avg_yield || 9.01,
                };
            case 'sectorAllocation':
                return clientData.sector_allocation || fetchFallbackData('sectorAllocation');
            default:
                return fetchFallbackData(widgetType);
        }
    } catch (error) {
        return fetchFallbackData(widgetType);
    }
};

// SQL source - use SQL_AGENT API
const fetchSqlData = async (widgetType, userId, useCaseId) => {
    try {
        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const activeCustomerId = getCustomerId(userId);
        const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
        const formData = new FormData();

        let question;
        switch (widgetType) {
            case 'performance':
                question = `Get the quarterly rate of returns for customer ${activeCustomerId} from v_quarterly_returns table. Return year, quarter, and rate_of_return as a JSON array ordered by year and quarter ascending.`;
                break;
            case 'assetPerformance':
                question = `Get the top 5 holdings by investment value for customer ${activeCustomerId} from v_holdings table. For each, show symbol, asset_name, and calculate rate_of_return as: (CAST(investment_value AS FLOAT) / 100000.0) to get a realistic percentage between -10 and 30. Format as JSON array with numeric rate_of_return values.`;
                break;
            case 'sectorAllocation':
                question = `For customer ${activeCustomerId}, return asset category as a flat JSON array. Each element must have exactly two fields: "sector" (string, the category name) and "allocation_value" (number, the percentage as a decimal without % symbol). Example: [{"sector": "Technology", "allocation_value": 31.88}, {"sector": "Equity", "allocation_value": 25.0}].`;
                console.log('[AssetAllocation] Prompt:', question);
                break;
            case 'portfolioHoldings':
                question = `Show me the portfolio holdings, their respective asset types, and their allocation percentages [100 * investment_value / total assets], group by asset type for ${activeCustomerId} in json format.`;
                break;
            default:
                return fetchFallbackData(widgetType);
        }

        formData.append('question', question);
        formData.append('cust_id', activeCustomerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);
        formData.append('agent_id', "63493");

        const response = await axios.post(SQL_API, formData);
        if (widgetType === 'sectorAllocation') {
            console.log('[AssetAllocation] Raw API response:', response.data);
            console.log('[AssetAllocation] Raw result string:', response.data?.response?.result);
        }

        if (response.data?.response?.result) {
            try {
                let rawResult = response.data.response.result;
                let parsedData;
                try {
                    parsedData = JSON.parse(rawResult);
                } catch (_) {
                    // Repair unquoted keys, e.g. {ESG Funds: "35.00%"} → {"ESG Funds": "35.00%"}
                    const repaired = rawResult.replace(
                        /([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_ $]*)\s*:/g,
                        (_, prefix, key) => `${prefix}"${key.trim()}":`
                    );
                    parsedData = JSON.parse(repaired);
                }

                // Unwrap single-item arrays where the value is a nested JSON string
                // e.g. [{ holdings_by_asset_type: "[{...}]" }] → [{...}]
                if (Array.isArray(parsedData) && parsedData.length === 1) {
                    const keys = Object.keys(parsedData[0]);
                    if (keys.length === 1) {
                        const val = parsedData[0][keys[0]];
                        if (typeof val === 'string') {
                            try {
                                const inner = JSON.parse(val);
                                if (Array.isArray(inner)) {
                                    parsedData = inner;
                                } else if (inner && typeof inner === 'object') {
                                    // e.g. { sector_allocation_breakdown: [...] }
                                    const innerVals = Object.values(inner);
                                    if (innerVals.length === 1 && Array.isArray(innerVals[0])) {
                                        parsedData = innerVals[0];
                                    } else if (widgetType === 'sectorAllocation') {
                                        // {sector: value} flat object nested inside wrapper
                                        parsedData = Object.entries(inner).map(([sector, value]) => ({
                                            sector,
                                            allocation_value: typeof value === 'number' ? value : parseFloat(value) || 0
                                        }));
                                    }
                                }
                            } catch (_) { }
                        }
                    }
                }

                // Normalize sectorAllocation to [{sector, allocation_value}] regardless of SQL Server response shape
                if (widgetType === 'sectorAllocation') {
                    if (Array.isArray(parsedData)) {
                        // Unwrap SQL Server JSON wrapper: [{JSON_GUID: json_string}, ...]
                        const firstKey = parsedData[0] && Object.keys(parsedData[0]).find(k => /^JSON_/i.test(k));
                        if (firstKey) {
                            const unwrapped = [];
                            for (const item of parsedData) {
                                try {
                                    const inner = typeof item[firstKey] === 'string' ? JSON.parse(item[firstKey]) : item[firstKey];
                                    if (Array.isArray(inner)) unwrapped.push(...inner);
                                    else if (inner && typeof inner === 'object') unwrapped.push(inner);
                                } catch (_) { }
                            }
                            parsedData = unwrapped;
                        }
                        // Normalize field names to {sector, allocation_value}
                        const standardKeys = ['sector', 'sector_name', 'allocation', 'allocation_value', 'allocation_percentage', 'percentage'];
                        parsedData = parsedData.flatMap(item => {
                            const hasStandardKey = Object.keys(item).some(k => standardKeys.includes(k));
                            if (!hasStandardKey) {
                                // Dynamic sector name as key: {"ESG Funds": "35.00%"}
                                return Object.entries(item).map(([sectorName, value]) => ({
                                    sector: sectorName,
                                    allocation_value: parseFloat(String(value).replace('%', '')) || 0
                                }));
                            }
                            return [{
                                sector: item.sector ?? item.sector_name ?? item.allocation,
                                allocation_value: item.allocation_value != null
                                    ? item.allocation_value
                                    : parseFloat(item.allocation_percentage ?? item.percentage ?? 0)
                            }];
                        });
                    } else if (parsedData && typeof parsedData === 'object') {
                        // Flat {sector: value} object
                        parsedData = Object.entries(parsedData).map(([sector, value]) => ({
                            sector,
                            allocation_value: typeof value === 'number' ? value : parseFloat(value) || 0
                        }));
                    }
                    console.log('[AssetAllocation] Normalized data:', parsedData);
                }
                if (Array.isArray(parsedData) && parsedData.length === 0) {
                    return fetchFallbackData(widgetType);
                }
                return parsedData;
            } catch (parseErr) {
                console.error(`[SectorAllocation] JSON parse error for ${widgetType}:`, parseErr);
                throw new Error("Could not parse data");
            }
        } else if (response.data?.answer) {
            const jsonMatch = response.data.answer.match(/```json\n([\s\S]*?)\n```/);
            if (jsonMatch?.[1]) {
                const parsedData = JSON.parse(jsonMatch[1]);
                return parsedData;
            } else {
                // Try to parse the answer directly as JSON
                try {
                    const parsedData = JSON.parse(response.data.answer);
                    return parsedData;
                } catch (e) {
                    throw new Error("Could not parse data");
                }
            }
        }

        throw new Error("No valid data in response");
    } catch (error) {
        return fetchFallbackData(widgetType);
    }
};

const fetchNovaData = async (widgetType, userId, useCaseId) => {
    try {
        // Check cache first
        const cacheKey = `${widgetType}_${userId}`;
        const cached = novaCache.get(cacheKey);

        if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
            return cached.data;
        }

        let prompt;

        switch (widgetType) {
            case 'projections':
                prompt = `Get portfolio projections for user ${userId} for years 2030 and 2040`;
                break;
            case 'marketSentiment':
                prompt = "What is the current market fear and greed index? Provide a value between 0-100 where 0 is extreme fear and 100 is extreme greed. Just respond with the number and a brief label (Extreme Fear, Fear, Neutral, Greed, or Extreme Greed).";
                break;
            default:
                return fetchFallbackData(widgetType);
        }

        // Get or generate thread ID
        let threadID = sessionStorage.getItem("wealth_thread_id");
        if (!threadID) {
            threadID = uuidv4();
            sessionStorage.setItem("wealth_thread_id", threadID);
        }

        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const formData = new FormData();
        formData.append("query", prompt);
        formData.append("cust_id", userId);
        formData.append("advisor_id", storedUserId);
        formData.append("use_case_id", useCaseId);
        formData.append("agent_id", "63493");
        formData.append("thread_id", threadID);

        // Add timeout to prevent hanging (30 seconds)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const response = await fetch(buildApiUrl('NOVA', '/Nova_chat_stream'), {
            method: "POST",
            body: formData,
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
        }

        // Process streaming response
        const result = await processStreamingResponse(response);

        // Parse the response based on widget type
        let parsedResult;
        if (widgetType === 'projections') {
            try {
                // Try to extract projection values from the response
                const projectionData = extractProjectionsFromText(result);
                parsedResult = projectionData || {
                    "2030": 3897502,
                    "2040": 6724009
                };
            } catch (e) {
                parsedResult = {
                    "2030": 3897502,
                    "2040": 6724009
                };
            }
        } else if (widgetType === 'marketSentiment') {
            parsedResult = result || "Neutral with positive outlook for tech sector";
        } else {
            parsedResult = result;
        }

        // Store in cache
        novaCache.set(cacheKey, {
            data: parsedResult,
            timestamp: Date.now()
        });

        return parsedResult;
    } catch (error) {
        if (error.name === 'AbortError') {
        }
        return fetchFallbackData(widgetType);
    }
};

// Helper function to process streaming response from NOVA
const processStreamingResponse = async (response) => {
    let accumulatedText = '';

    try {
        const { createParser } = await import('eventsource-parser');

        const parser = createParser({
            onEvent: (event) => {
                const type = event.event;
                if (type === "filler" || type === "interrupt" || type === "final") {
                    try {
                        const parsedData = JSON.parse(event.data);

                        if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                            throw new Error(parsedData.ERROR);
                        }

                        if (parsedData.data.response) {
                            accumulatedText += parsedData.data.response;
                        } else if (parsedData.data) {
                            accumulatedText += parsedData.data;
                        }
                    } catch (error) {
                    }
                }
            }
        });

        const reader = response.body.getReader();

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = new TextDecoder().decode(value);
            parser.feed(chunk);
        }

        try {
            return JSON.parse(accumulatedText);
        } catch (parseErr) {
            const jsonMatch = accumulatedText.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                try {
                    return JSON.parse(jsonMatch[0]);
                } catch (e) {
                    return accumulatedText;
                }
            } else {
                return accumulatedText;
            }
        }
    } catch (err) {
        throw new Error("Failed to process response");
    }
};

// Helper function to extract projection values from text
const extractProjectionsFromText = (text) => {
    if (typeof text !== 'string') {
        if (typeof text === 'object') return text;
        return null;
    }

    // Try to find numbers following "2030" and "2040"
    const projection2030Match = text.match(/2030[^\d]*(\$?[\d,]+)/i);
    const projection2040Match = text.match(/2040[^\d]*(\$?[\d,]+)/i);

    const projection2030 = projection2030Match ?
        parseInt(projection2030Match[1].replace(/[\$,]/g, '')) : 3897502;

    const projection2040 = projection2040Match ?
        parseInt(projection2040Match[1].replace(/[\$,]/g, '')) : 6724009;

    return {
        "2030": projection2030,
        "2040": projection2040
    };
};

// Fallback data when API calls fail
const fetchFallbackData = (widgetType) => {
    switch (widgetType) {
        case 'portfolio':
            return {
                totalAum: 2845000,
                ytdReturn: 12.8,
                avgYield: 9.01,
            };
        case 'performance':
            return {
                "user123": {
                    "2023": [2.1, 3.4, 4.2, 3.1],
                    "2024": [2.8, 3.2, 3.9, 4.5]
                }
            };
        case 'projections':
            return {
                "2030": 3897502,
                "2040": 6724009
            };
        case 'marketSentiment':
            return { value: 58, label: "Greed" };
        case 'assetPerformance':
            return [
                { asset: "AAPL", performance: 12.3 },
                { asset: "MSFT", performance: 8.7 },
                { asset: "AMZN", performance: -2.1 },
                { asset: "GOOGL", performance: 5.4 },
                { asset: "TSLA", performance: 15.2 }
            ];
        default:
            return null;
    }
};

export const fetchPortfolioHealth = async (userId, useCaseId = 'wealth_management') => {
    try {
        // Fetch actual portfolio data
        const [portfolioData, holdingsData, performanceData] = await Promise.all([
            fetchWidgetData('portfolio', 'DATABASE', userId, useCaseId),
            fetchWidgetData('portfolioHoldings', 'SQL', userId, useCaseId),
            fetchWidgetData('assetPerformance', 'SQL', userId, useCaseId)
        ]);

        // Calculate actual YTD return from portfolio data
        const ytdReturn = portfolioData?.ytdReturn || 12.8;

        // Calculate diversification score based on holdings
        let diversificationScore = 'B';
        let diversificationPoints = 60;

        if (Array.isArray(holdingsData) && holdingsData.length > 0) {
            const numAssetTypes = new Set(holdingsData.map(h => h.asset_type)).size;
            const numHoldings = holdingsData.length;

            // Calculate diversification based on number of asset types and holdings
            if (numAssetTypes >= 4 && numHoldings >= 5) {
                diversificationScore = 'A+';
                diversificationPoints = 95;
            } else if (numAssetTypes >= 3 && numHoldings >= 4) {
                diversificationScore = 'A';
                diversificationPoints = 85;
            } else if (numAssetTypes >= 2 && numHoldings >= 3) {
                diversificationScore = 'B+';
                diversificationPoints = 75;
            } else if (numAssetTypes >= 2) {
                diversificationScore = 'B';
                diversificationPoints = 65;
            } else {
                diversificationScore = 'C';
                diversificationPoints = 50;
            }
        }

        // Calculate beta - approximate based on equity vs fixed income allocation
        let beta = 1.0;
        if (Array.isArray(holdingsData) && holdingsData.length > 0) {
            const equityAllocation = holdingsData
                .filter(h => h.asset_type === 'Equity')
                .reduce((sum, h) => sum + (parseFloat(h.allocation_percentage) || 0), 0);

            // Approximate beta: higher equity = higher beta
            // Typical range: 0.5-1.5
            beta = 0.5 + (equityAllocation / 100) * 1.0;
            beta = Math.round(beta * 10) / 10; // Round to 1 decimal
        }

        // Calculate overall health score
        // Factors: diversification (40%), return performance (30%), risk balance (30%)
        const returnScore = Math.min(100, Math.max(0, (ytdReturn + 10) * 5)); // Map -10 to 30% as 0-100
        const riskScore = beta < 0.8 ? 90 : beta > 1.3 ? 70 : 85; // Prefer moderate risk

        const overallScore = Math.round(
            diversificationPoints * 0.4 +
            returnScore * 0.3 +
            riskScore * 0.3
        );

        // Determine rating based on score
        let rating = 'Fair';
        if (overallScore >= 90) rating = 'Excellent';
        else if (overallScore >= 80) rating = 'Very Good';
        else if (overallScore >= 70) rating = 'Good';
        else if (overallScore >= 60) rating = 'Fair';
        else rating = 'Needs Attention';

        return {
            score: overallScore,
            rating: rating,
            diversification: diversificationScore,
            beta: beta,
            ytdReturn: ytdReturn
        };
    } catch (error) {
        // Return reasonable fallback based on typical portfolio
        return {
            score: 78,
            rating: "Good",
            diversification: "B+",
            beta: 1.1,
            ytdReturn: 12.8
        };
    }
};

export const fetchAIInsights = async (userId, useCaseId = 'wealth_management') => {
    try {
        // Fetch portfolio data to generate personalized insights
        const [portfolioData, holdingsData, performanceData, healthData, goalsData, milestonesData] = await Promise.all([
            fetchWidgetData('portfolio', 'DATABASE', userId, useCaseId).catch(() => null),
            fetchWidgetData('portfolioHoldings', 'SQL', userId, useCaseId).catch(() => []),
            fetchWidgetData('assetPerformance', 'SQL', userId, useCaseId).catch(() => []),
            fetchPortfolioHealth(userId, useCaseId).catch(() => null),
            fetchFinancialGoals(userId, useCaseId).catch(() => []),
            fetchLifeEventsData(userId, useCaseId).catch(() => [])
        ]);

        const insights = [];

        // Parse real goals data from database
        const primaryGoal = goalsData && goalsData.length > 0 ? goalsData[0] : null;
        const hasRetirementGoal = primaryGoal && primaryGoal.goal_type?.toLowerCase().includes('retirement');
        const goalProgress = primaryGoal ? Math.round((parseFloat(primaryGoal.current_amount) / parseFloat(primaryGoal.target_amount)) * 100) : 0;

        // Parse recent life events
        const recentEvent = milestonesData && milestonesData.length > 0 ? milestonesData[0] : null;
        const hasPromotionEvent = recentEvent && recentEvent.milestone?.toLowerCase().includes('promotion');
        const hasVestingEvent = recentEvent && recentEvent.milestone?.toLowerCase().includes('vesting');

        // 1. GOAL-LINKED INSIGHTS - Highest Priority
        if (Array.isArray(holdingsData) && holdingsData.length > 0 && hasRetirementGoal) {
            const techHoldings = holdingsData.filter(h =>
                ['AAPL', 'AMZN', 'GOOGL', 'MSFT', 'NVDA', 'TSM'].includes(h.symbol)
            );

            if (techHoldings.length >= 2) {
                const goalName = primaryGoal.goal_name || 'retirement';
                const targetYear = primaryGoal.target_date ? new Date(primaryGoal.target_date).getFullYear() : 'your target';
                insights.push({
                    type: 'growth_opportunity',
                    title: 'Tech Allocation Supports Retirement Goal',
                    description: `Your ${techHoldings.length} growth-oriented tech holdings align with your ${goalName} timeline (${targetYear}). Current trajectory shows ${goalProgress}% progress toward target.`,
                    action: 'Review growth strategy →',
                    opportunity: 'ENGAGEMENT',
                    category: 'Goal-Aligned Strategy'
                });
            }
        }

        // 2. EVENT-TRIGGERED INSIGHTS - Recent life events
        if (hasPromotionEvent || hasVestingEvent) {
            const eventType = hasPromotionEvent ? 'promotion' : 'stock options vesting';
            insights.push({
                type: 'tax_optimization',
                title: `${hasPromotionEvent ? 'Promotion' : 'Stock Vesting'} - Tax Opportunity`,
                description: `With your recent ${eventType}, consider maximizing 401k contributions to $23,000 limit and exploring backdoor Roth conversions for tax efficiency.`,
                action: 'Review tax strategy →',
                opportunity: 'CROSS_SELL',
                category: 'Tax Planning Services'
            });
        }

        // 3. PERSONALITY-BASED INSIGHTS - Based on risk profile
        const equityAllocation = Array.isArray(holdingsData) ? holdingsData
            .filter(h => h.asset_type === 'Equity')
            .reduce((sum, h) => sum + (parseFloat(h.allocation_percentage) || 0), 0) : 0;

        if (equityAllocation > 60 && equityAllocation < 80) {
            insights.push({
                type: 'growth_opportunity',
                title: 'Growth-Oriented Portfolio Opportunity',
                description: `As a growth-oriented investor with 15+ year horizon, your ${equityAllocation.toFixed(0)}% equity allocation positions you well. Consider emerging markets exposure for additional diversification.`,
                action: 'Explore emerging markets →',
                opportunity: 'UPSELL',
                category: 'International Growth'
            });
        }

        // 4. Portfolio Diversification Insight
        if (Array.isArray(holdingsData) && holdingsData.length > 0) {
            const assetTypes = [...new Set(holdingsData.map(h => h.asset_type))];

            if (equityAllocation > 75) {
                insights.push({
                    type: 'risk_management',
                    title: 'High Equity Concentration',
                    description: `Your equity allocation at ${equityAllocation.toFixed(0)}% may be higher than optimal for risk management. Consider rebalancing into fixed income or alternative assets to reduce volatility.`,
                    action: 'Review allocation →',
                    opportunity: 'CROSS_SELL',
                    category: 'Fixed Income'
                });
            }
        }

        // 5. Performance-Based Insights
        if (Array.isArray(performanceData) && performanceData.length > 0) {
            const topPerformer = performanceData.reduce((best, asset) =>
                (asset.rate_of_return > best.rate_of_return) ? asset : best
            );
            const underperformer = performanceData.reduce((worst, asset) =>
                (asset.rate_of_return < worst.rate_of_return) ? asset : worst
            );

            if (topPerformer.rate_of_return > 20) {
                insights.push({
                    type: 'rebalancing',
                    title: 'Rebalancing Opportunity',
                    description: `${topPerformer.symbol} up ${topPerformer.rate_of_return.toFixed(1)}%. Consider rebalancing to maintain target allocation and lock in gains toward retirement goal.`,
                    action: 'Schedule rebalancing →',
                    opportunity: 'ENGAGEMENT',
                    category: 'Portfolio Management'
                });
            }

            if (underperformer.rate_of_return < -5 && insights.length < 4) {
                insights.push({
                    type: 'tax_optimization',
                    title: 'Tax-Loss Harvesting Available',
                    description: `${underperformer.symbol} down ${Math.abs(underperformer.rate_of_return).toFixed(1)}%. Opportunity to harvest losses for tax optimization while staying invested.`,
                    action: 'Review tax strategy →',
                    opportunity: 'CROSS_SELL',
                    category: 'Tax Planning Services'
                });
            }
        }

        // 6. AUM-Based Advisory Opportunities
        if (portfolioData?.totalAum) {
            const aum = portfolioData.totalAum;

            if (aum > 2000000 && aum < 5000000 && insights.length < 4) {
                insights.push({
                    type: 'premium_services',
                    title: 'Eligible for Private Wealth Management',
                    description: `With $${(aum / 1000000).toFixed(1)}M AUM, you qualify for Private Wealth tier. Enhanced services and preferential pricing available.`,
                    action: 'Upgrade services →',
                    opportunity: 'UPSELL',
                    category: 'Private Wealth Management'
                });
            }
        }

        // 7. Health Score-Based Recommendations
        if (healthData && insights.length < 4) {
            if (healthData.score < 70) {
                insights.push({
                    type: 'health_improvement',
                    title: 'Portfolio Optimization Opportunity',
                    description: `Your portfolio health score of ${healthData.score} indicates opportunity for optimization. Schedule a review with your advisor to improve diversification and risk management.`,
                    action: 'Book advisor meeting →',
                    opportunity: 'ENGAGEMENT',
                    category: 'Advisory Services'
                });
            }

            if (healthData.beta > 1.3 && insights.length < 4) {
                insights.push({
                    type: 'risk_reduction',
                    title: 'High Market Risk Detected',
                    description: `Your portfolio beta of ${healthData.beta} shows elevated market correlation. Consider adding uncorrelated assets to reduce volatility${hasRetirementGoal ? ' and protect your retirement timeline' : ''}.`,
                    action: 'Explore alternatives →',
                    opportunity: 'CROSS_SELL',
                    category: 'Alternative Investments'
                });
            }
        }

        // Ensure at least 4 insights
        if (insights.length < 4) {
            const defaultInsights = [
                {
                    type: 'market_outlook',
                    title: 'Q1 2026 Market Positioning',
                    description: 'Economic indicators suggest defensive positioning. Recommend increasing allocation to stable sectors and quality bonds to protect gains.',
                    action: 'View market report →',
                    opportunity: 'ENGAGEMENT',
                    category: 'Market Insights'
                },
                {
                    type: 'retirement_planning',
                    title: 'Retirement Readiness Check',
                    description: 'Annual retirement planning review recommended to ensure client remains on track for 2037 early retirement goal with current market conditions.',
                    action: 'Plan retirement →',
                    opportunity: 'CROSS_SELL',
                    category: 'Retirement Planning'
                }
            ];
            insights.push(...defaultInsights.slice(0, 4 - insights.length));
        }

        return insights.slice(0, 4); // Return top 4 insights
    } catch (error) {
        return [
            {
                type: 'rebalancing',
                title: 'Quarterly Portfolio Review',
                description: 'Schedule quarterly review with client to ensure alignment with investment objectives and current market conditions.',
                action: 'Book review →',
                opportunity: 'ENGAGEMENT'
            },
            {
                type: 'goal_tracking',
                title: 'Retirement Goal Progress',
                description: 'Client on track for 2037 early retirement goal. Continue current savings rate and portfolio strategy.',
                action: 'View progress →',
                opportunity: 'ENGAGEMENT'
            },
            {
                type: 'tax_optimization',
                title: 'Year-End Tax Planning',
                description: 'Optimize client tax position with strategic harvesting and contribution strategies before year-end.',
                action: 'Tax consultation →',
                opportunity: 'CROSS_SELL'
            },
            {
                type: 'growth',
                title: 'Growth Opportunities',
                description: 'Explore emerging market and sector-specific opportunities aligned with client risk tolerance and retirement timeline.',
                action: 'Explore options →',
                opportunity: 'UPSELL'
            }
        ];
    }
};

export const fetchHoldingsData = async (userId) => {
    try {
        return fetchWidgetData('portfolioHoldings', 'SQL', userId, 'wealth_management');

    } catch (error) {
        return [];
    }
};

// Fetch life events/milestones data from v_personal_milestones
export const fetchLifeEventsData = async (userId, useCaseId = 'wealth_management') => {
    try {
        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const activeCustomerId = getCustomerId(userId);
        const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
        const formData = new FormData();

        const question = `Get all milestones from v_personal_milestones for customer ${activeCustomerId} ordered by reminder_date ASC. Return milestone, note, reminder_date, milestone_id as JSON array. Limit to next 5 upcoming milestones.`;


        formData.append('question', question);
        formData.append('cust_id', activeCustomerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);
        formData.append('agent_id', "63493");

        const response = await axios.post(SQL_API, formData);


        if (response.data?.response?.result) {
            try {
                const parsedData = JSON.parse(response.data.response.result);
                return parsedData;
            } catch (parseErr) {
                return [];
            }
        }

        return [];
    } catch (error) {
        return [];
    }
};

// Fetch advisor meeting data from v_meetings
export const fetchAdvisorMeetingsData = async (userId, useCaseId = 'wealth_management') => {
    try {
        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const activeCustomerId = getCustomerId(userId);
        const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
        const formData = new FormData();

        const question = `SELECT meeting_id, title, meeting_date, start_time, end_time, advisor_id, meeting_type, status, notes, created_at FROM v_meetings WHERE customer_id = '${activeCustomerId}' ORDER BY meeting_date ASC`;

        formData.append('question', question);
        formData.append('cust_id', activeCustomerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);
        formData.append('agent_id', "63493");

        const response = await axios.post(SQL_API, formData);

        if (response.data?.response?.result) {
            try {
                const parsedData = JSON.parse(response.data.response.result);
                if (Array.isArray(parsedData) && parsedData.length > 0) {
                }

                return parsedData;
            } catch (parseErr) {

                return [];
            }
        }

        return [];
    } catch (error) {

        return [];
    }
};

// Fetch advisor details from v_advisors
export const fetchAdvisorDetailsData = async (advisorId, userId, useCaseId = 'wealth_management') => {
    try {
        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const activeCustomerId = getCustomerId(userId);
        const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
        const formData = new FormData();

        const question = `Get advisor details from v_advisors where advisor_id = '${advisorId}'. Return advisor_id, advisor_name, email, phone, designation, specialization, region, service_tier as JSON object.`;

        formData.append('question', question);
        formData.append('cust_id', activeCustomerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);
        formData.append('agent_id', "63493");

        const response = await axios.post(SQL_API, formData);

        if (response.data?.response?.result) {
            try {
                const parsedData = JSON.parse(response.data.response.result);

                const result = Array.isArray(parsedData) ? parsedData[0] : parsedData;

                // If result is array, return first item; otherwise return as is
                return result;
            } catch (parseErr) {

                return null;
            }
        }

        return null;
    } catch (error) {

        return null;
    }
};

// Fetch benchmark data for portfolio comparison
export const fetchBenchmarkData = async (benchmarkId = 'SP500', startDate = null, endDate = null, userId = null) => {
    try {
        const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
        const formData = new FormData();
        const storedUserId = sessionStorage.getItem('user_id') || userId || 'default';
        const activeCustomerId = getCustomerId(userId);

        let query = `SELECT benchmark_id, benchmark_name, benchmark_type, date, adj_close 
                     FROM v_benchmarks 
                     WHERE benchmark_id = '${benchmarkId}'`;

        if (startDate) {
            query += ` AND date >= '${startDate}'`;
        }
        if (endDate) {
            query += ` AND date <= '${endDate}'`;
        }

        query += ` ORDER BY date ASC`;

        formData.append('question', query);
        formData.append('cust_id', activeCustomerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', '1');
        formData.append('agent_id', '63493');

        const response = await axios.post(SQL_API, formData);

        if (response.data?.response?.result) {
            const parsedData = JSON.parse(response.data.response.result);
            return Array.isArray(parsedData) ? parsedData : [parsedData];
        }

        return [];
    } catch (error) {
        return [];
    }
};

// Fetch financial goals data
export const fetchFinancialGoals = async (userId, useCaseId) => {
    try {
        const activeCustomerId = getCustomerId(userId);
        const storedUserId = sessionStorage.getItem('user_id') || userId;
        const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
        const formData = new FormData();

        const query = `SELECT goal_id, goal_name, goal_type, target_amount, current_amount, 
                              target_date, created_date, status
                       FROM v_financial_goals 
                       WHERE customer_id = '${activeCustomerId}'
                       ORDER BY target_date ASC`;

        formData.append('question', query);
        formData.append('cust_id', activeCustomerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);
        formData.append('agent_id', '63493');

        const response = await axios.post(SQL_API, formData);

        if (response.data?.response?.result) {
            const parsedData = JSON.parse(response.data.response.result);
            return Array.isArray(parsedData) ? parsedData : [parsedData];
        }

        return [];
    } catch (error) {
        return [];
    }
};